import { makeAutoObservable, runInAction } from "mobx";
import {
  createSwayyPriceList,
  isCancelledSwayyRequest,
  listSwayyPriceLists,
  normalizeSwayyApiError,
  SwayyApiError,
  updateSwayyPriceList,
} from "../api/SwayyProductsAPI";
import { SwayyCatalogLoadState } from "../models/common/SwayyCatalogLoadState";
import {
  SwayyPriceList,
  SwayyPriceListType,
} from "../models/pricing/SwayyPriceList";
import {
  createPriceListFormSchema,
  updatePriceListFormSchema,
} from "../validation/swayyFormSchemas";

export type SwayyCreatePriceListForm = {
  name: string;
  type: SwayyPriceListType;
  currency: string;
  isDefault: boolean;
};

export type SwayyEditPriceListForm = {
  name: string;
  currency: string;
  isDefault: boolean;
  isActive: boolean;
};

const emptyCreateForm = (): SwayyCreatePriceListForm => ({
  name: "",
  type: "b2c",
  currency: "INR",
  isDefault: true,
});

export class SwayyPriceListsStore {
  lists: SwayyPriceList[] = [];
  listState: SwayyCatalogLoadState = "idle";
  errorMessage = "";
  isSaving = false;
  searchDraft = "";
  search = "";
  createForm: SwayyCreatePriceListForm = emptyCreateForm();
  editForm: SwayyEditPriceListForm | null = null;
  editingId: number | null = null;
  private listAbort?: AbortController;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get filteredLists(): SwayyPriceList[] {
    const q = this.search.trim().toLowerCase();
    if (!q) return this.lists;
    return this.lists.filter(
      (list) =>
        list.name.toLowerCase().includes(q) ||
        list.type.toLowerCase().includes(q) ||
        list.currency.toLowerCase().includes(q),
    );
  }

  get hasDefaultB2c(): boolean {
    return this.lists.some(
      (list) => list.type === "b2c" && list.isDefault && list.isActive,
    );
  }

  get canCreate(): boolean {
    return createPriceListFormSchema.safeParse(this.createForm).success;
  }

  get canSaveEdit(): boolean {
    if (!this.editForm) return false;
    return updatePriceListFormSchema.safeParse(this.editForm).success;
  }

  setSearchDraft(value: string): void {
    this.searchDraft = value;
  }

  setSearch(value: string): void {
    this.search = value;
  }

  updateCreateForm<K extends keyof SwayyCreatePriceListForm>(
    key: K,
    value: SwayyCreatePriceListForm[K],
  ): void {
    this.createForm = { ...this.createForm, [key]: value };
  }

  resetCreateForm(defaults?: Partial<SwayyCreatePriceListForm>): void {
    this.createForm = { ...emptyCreateForm(), ...defaults };
  }

  /** Prefill create dialog for one-click Default B2C. */
  prepareDefaultB2cCreate(): void {
    this.createForm = {
      name: "Default B2C",
      type: "b2c",
      currency: "INR",
      isDefault: true,
    };
  }

  startEdit(list: SwayyPriceList): void {
    this.editingId = list.id;
    this.editForm = {
      name: list.name,
      currency: list.currency,
      isDefault: list.isDefault,
      isActive: list.isActive,
    };
  }

  updateEditForm<K extends keyof SwayyEditPriceListForm>(
    key: K,
    value: SwayyEditPriceListForm[K],
  ): void {
    if (!this.editForm) return;
    this.editForm = { ...this.editForm, [key]: value };
  }

  clearEdit(): void {
    this.editingId = null;
    this.editForm = null;
  }

  async loadLists(): Promise<SwayyApiError | null> {
    this.listAbort?.abort();
    const controller = new AbortController();
    this.listAbort = controller;
    this.listState = "loading";
    this.errorMessage = "";
    try {
      const lists = await listSwayyPriceLists(controller.signal);
      if (controller.signal.aborted) return null;
      runInAction(() => {
        this.lists = lists;
        this.listState = "ready";
      });
      return null;
    } catch (error) {
      if (isCancelledSwayyRequest(error)) return null;
      const normalized = normalizeSwayyApiError(
        error,
        "Unable to load price lists.",
      );
      runInAction(() => {
        this.listState = "error";
        this.errorMessage = normalized.message;
      });
      return normalized;
    }
  }

  async createList(): Promise<{
    error: SwayyApiError | null;
    listId: number | null;
  }> {
    const parsed = createPriceListFormSchema.safeParse(this.createForm);
    if (!parsed.success) {
      return {
        error: new SwayyApiError(
          parsed.error.issues[0]?.message ?? "Invalid price list.",
          400,
        ),
        listId: null,
      };
    }
    this.isSaving = true;
    try {
      const created = await createSwayyPriceList({
        name: parsed.data.name,
        type: parsed.data.type,
        currency: parsed.data.currency,
        is_default: parsed.data.isDefault,
      });
      runInAction(() => {
        this.lists = [created, ...this.lists.filter((l) => l.id !== created.id)];
        if (created.isDefault) {
          this.lists = this.lists.map((list) =>
            list.id !== created.id && list.type === created.type
              ? { ...list, isDefault: false }
              : list,
          );
        }
        this.resetCreateForm();
      });
      await this.loadLists();
      return { error: null, listId: created.id };
    } catch (error) {
      return {
        error: normalizeSwayyApiError(error, "Unable to create price list."),
        listId: null,
      };
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  }

  async saveEdit(): Promise<SwayyApiError | null> {
    if (this.editingId == null || !this.editForm) {
      return new SwayyApiError("No price list selected.", 400);
    }
    const parsed = updatePriceListFormSchema.safeParse(this.editForm);
    if (!parsed.success) {
      return new SwayyApiError(
        parsed.error.issues[0]?.message ?? "Invalid price list.",
        400,
      );
    }
    this.isSaving = true;
    try {
      const updated = await updateSwayyPriceList(this.editingId, {
        name: parsed.data.name,
        currency: parsed.data.currency,
        is_default: parsed.data.isDefault,
        is_active: parsed.data.isActive,
      });
      runInAction(() => {
        this.lists = this.lists.map((list) => {
          if (list.id === updated.id) return updated;
          if (updated.isDefault && list.type === updated.type) {
            return { ...list, isDefault: false };
          }
          return list;
        });
        this.clearEdit();
      });
      return null;
    } catch (error) {
      return normalizeSwayyApiError(error, "Unable to update price list.");
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  }

  dispose(): void {
    this.listAbort?.abort();
  }
}
